// ============================================================================
// UnsubscribePage — landed via #unsubscribe/<token>. (Task #9)
// ============================================================================
// One-click unsubscribe (no confirm prompt — CAN-SPAM friendliness). Idempotent
// on the server (multiple clicks flip to/stay unsubscribed).
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, MailMinus, AlertCircle } from 'lucide-react';
import { api } from '../api';

interface Props {
  token: string;
}

export function UnsubscribePage({ token }: Props) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.unsubscribe(token)
      .then((r) => {
        if (cancelled) return;
        setStatus('ok');
        setEmail(r.email);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setError(err?.message || 'Could not unsubscribe.');
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
        </div>
      </header>
      <main className="flex-1 max-w-md mx-auto px-4 py-12 w-full">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-10 w-10 text-gray-500 mx-auto animate-spin" />
              <p className="text-sm text-gray-600 mt-3">Unsubscribing…</p>
            </>
          )}
          {status === 'ok' && (
            <>
              <MailMinus className="h-12 w-12 text-gray-600 mx-auto" />
              <h1 className="text-xl font-bold text-gray-900 mt-2">You're unsubscribed</h1>
              <p className="text-sm text-gray-700 mt-2">
                {email && <>We won't email <strong>{email}</strong> any more.</>}
                {' '}If this was a mistake, you can re-subscribe any time from the homepage.
              </p>
              <a
                href="/"
                className="inline-block mt-4 rounded-md bg-uif-green text-white font-semibold px-4 py-2 hover:bg-uif-green-dark"
              >
                Back to the calendar
              </a>
            </>
          )}
          {status === 'error' && (
            <>
              <AlertCircle className="h-10 w-10 text-red-600 mx-auto" />
              <h1 className="text-xl font-bold text-red-800 mt-2">Unsubscribe link not recognized</h1>
              <p className="text-sm text-gray-700 mt-2">{error}</p>
              <a
                href="mailto:support@upinfarms.com"
                className="inline-block mt-4 rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Email support
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
